"use client";

import { useEffect, useRef } from "react";
import styled from "styled-components";
import Lock from "@churchofjesuschrist/eden-body-lock";
import { grey2, black, setAppearanceConstantLocal } from "@churchofjesuschrist/eden-style-constants";
import Card from "@churchofjesuschrist/eden-card";
import { adjustHexAlpha } from "@churchofjesuschrist/eden-utilities";
import { mediaBreak480w } from "@churchofjesuschrist/eden-styled-helpers";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const [platformModalBackdropBackgroundDef, platformModalBackdropBackground] = setAppearanceConstantLocal("alert-modal-backdrop-background", {
  light: adjustHexAlpha(grey2, 0.6),
  dark: adjustHexAlpha(black, 0.6),
  prefix: "eden"
});
const Dialog = styled(Card).withConfig({
  componentId: "sc-7cm3zw-0"
})(["display:flex;flex-direction:column;visibility:hidden;overflow:visible;padding:0;color:inherit;width:100vw;max-width:100dvw;max-height:100dvh;&[open]{visibility:visible;}&::backdrop{", ";backdrop-filter:blur(2px);background:", ";}", ";"], platformModalBackdropBackgroundDef, platformModalBackdropBackground, mediaBreak480w`
    width: fit-content;
    max-width: 34.375rem;
    min-width: 25rem;
    max-height: calc(100vh - 6rem);
  `);

// This is to suppress warnings related to not being able to use the `autoFocus` prop due to this bug: https://github.com/facebook/react/issues/23301
if (typeof console !== "undefined" && typeof console.error === "function") {
  const consoleError = console.error;
  console.error = function filterWarnings(...args) {
    if (!args.includes("autoFocus")) {
      consoleError(...args);
    }
  };
}

/**
 * A low-level component for displaying modal dialog content.
 * @param {object} props - The component props
 * @param {React.ReactNode} [props.children] - Modal content
 * @param {boolean} [props.open] - Whether modal is open
 * @param {Function} props.onClose - Callback to execute when a close method is triggered
 * @returns {React.ReactElement} The PlatformModal component
 */
const PlatformModal = ({
  children,
  open,
  onClose,
  ...props
}) => {
  const dialogRef = useRef();
  useEffect(() => {
    const dialog = dialogRef.current;
    if (open) {
      dialog.returnValue = "";
      dialog.showModal?.();
    } else {
      // Custom value set on returnValue to detect if close is coming from open prop change
      dialog.returnValue = "eden-platform-modal-closed";
      dialog.close?.();
    }
  }, [open]);

  /**
   * Handles the close event, and the cancel event where a user attempts to dismiss the dialog via the Escape key
   * @param {Event} event - The cancel event from the dialog.
   * @returns {void}
   */
  const handleCloseAndCancel = event => {
    event.preventDefault(); // Prevents the default behavior of native close and cancel events so that control is left up to the `open` prop.
    event.stopPropagation(); // Prevent event from bubbling up in the case of HTML nested dialogs

    if (dialogRef.current.returnValue !== "eden-platform-modal-closed") {
      onClose(event);
    }
  };

  /**
   * Handles clicks on the backdrop to close the modal.
   * @param {MouseEvent} event - The click event on the dialog backdrop.
   * @returns {void}
   */
  const handleBackdropClick = event => {
    if (event.target === dialogRef.current) {
      onClose(event);
    }
  };
  return /*#__PURE__*/_jsxs(Dialog, {
    ref: dialogRef,
    onClick: handleBackdropClick,
    onClose: handleCloseAndCancel,
    onCancel: handleCloseAndCancel,
    renderAs: "dialog",
    ...props,
    children: [/*#__PURE__*/_jsx(Lock, {
      active: open
    }), children]
  });
};
PlatformModal.displayName = "PlatformModal";
export default PlatformModal;